import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { companiesAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import { logoUrl } from '../utils/companyLogo';

// Upload + preview pentru două variante de logo (light + dark). Dacă una lipsește,
// cealaltă se va folosi automat pe ambele teme — fallback-ul e pe UI în render.
const LogoSlot = ({
  _variant,
  label,
  hint,
  pathValue,
  preview,
  dark,
  onPick,
  onRemove,
  saving,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const url = pathValue ? logoUrl(pathValue) : null;

  const slotBg = dark
    ? 'bg-slate-900 border-slate-700'
    : 'bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700';

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{label}</span>
        {pathValue && (
          <button
            type="button"
            onClick={onRemove}
            disabled={saving}
            className="text-xs text-danger-600 dark:text-danger-300 hover:underline disabled:opacity-50"
          >
            {t('common.delete')}
          </button>
        )}
      </div>
      <div
        className={`flex items-center justify-center h-32 rounded-lg border-2 border-dashed ${slotBg} overflow-hidden`}
      >
        {preview ? (
          <img src={preview} alt={label} className="max-h-full max-w-full object-contain" />
        ) : url ? (
          <img src={url} alt={label} className="max-h-full max-w-full object-contain" />
        ) : (
          <span
            className={`text-xs ${dark ? 'text-slate-500' : 'text-slate-400 dark:text-slate-500'}`}
          >
            {t('companyLogo.noLogo')}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          e.target.value = '';
        }}
      />
      <div className="mt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={saving}
          className="w-full"
        >
          {pathValue ? t('companyLogo.replace') : t('common.upload')}
        </Button>
      </div>
    </div>
  );
};

const CompanyLogoManager = ({ companyId, logoLight, logoDark, onChange }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user, refreshUser } = useAuth();
  const [preview, setPreview] = useState({ light: null, dark: null });
  const [saving, setSaving] = useState(false);

  // Dacă firma editată e propria firmă a user-ului, refresh user context după logo change
  // ca să apară imediat în bara de sus / sidebar — fără deconectare.
  const ownCompanyEdit = user?.companyId && companyId === user.companyId;

  const handleUpload = async (variant, file) => {
    if (file.size > 3 * 1024 * 1024) {
      showToast(t('companyLogo.fileTooLarge'), 'error');
      return;
    }
    // Preview local imediat
    const reader = new FileReader();
    reader.onload = (ev) => setPreview((p) => ({ ...p, [variant]: ev.target.result }));
    reader.readAsDataURL(file);

    setSaving(true);
    try {
      const res = await companiesAPI.uploadLogo(companyId, variant, file);
      setPreview({ light: null, dark: null });
      onChange?.({ logoLight: res.data.logoLight, logoDark: res.data.logoDark });
      if (ownCompanyEdit) await refreshUser();
      showToast(t('companyLogo.uploadSuccess'), 'success');
    } catch (err) {
      setPreview((p) => ({ ...p, [variant]: null }));
      showToast(err.response?.data?.error || t('companyLogo.uploadError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (variant) => {
    setSaving(true);
    try {
      const res = await companiesAPI.deleteLogo(companyId, variant);
      onChange?.({ logoLight: res.data.logoLight, logoDark: res.data.logoDark });
      if (ownCompanyEdit) await refreshUser();
      showToast(t('companyLogo.deleteSuccess'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('companyLogo.deleteError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t('companyLogo.title')}
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {t('companyLogo.description')}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <LogoSlot
          variant="light"
          label={t('companyLogo.lightLabel')}
          hint={t('companyLogo.lightHint')}
          pathValue={logoLight}
          preview={preview.light}
          dark={false}
          onPick={(f) => handleUpload('light', f)}
          onRemove={() => handleDelete('light')}
          saving={saving}
        />
        <LogoSlot
          variant="dark"
          label={t('companyLogo.darkLabel')}
          hint={t('companyLogo.darkHint')}
          pathValue={logoDark}
          preview={preview.dark}
          dark={true}
          onPick={(f) => handleUpload('dark', f)}
          onRemove={() => handleDelete('dark')}
          saving={saving}
        />
      </div>
    </div>
  );
};

export default CompanyLogoManager;
